/**
 * weapon-evolution.ts — 場內武器進化 (R-004 包 C),Phaser-free 純資料。
 *
 * 設計動機 (boss:「沒有越打越強的感覺」→ 場內要有質變):升級卡原本只線性加值,
 * 缺「質變」的爽感。這裡讓「特定升級卡疊到門檻 (同卡 3 次) 或集滿指定兩卡組合」→
 * 武器進化成強化形態 (視覺 + 數值質變,例:重砲 ×3 → 貫穿彈、散彈+爆炸 → 爆裂散彈)。
 *
 * 資料驅動:
 *   - 配方門檻 + 文案 + feedback preset 名 → weapon-evolution-recipes.json (本檔 import)。
 *   - 進化後的數值質變 → balance config 的 weaponEvolution (跟既有 balance loader 一致)。
 *   不在這裡 inline 任何門檻數字或數值。
 *
 * 鐵則 (學習與遊戲分離,最高優先):
 *   - 進化純當局戰力:只讀/寫 RunModifiers (跟升級卡/synergy 同性質,跨場 new 歸零)。
 *   - 絕不碰 progress / factionXP / freeXP / skillLevels / 永久戰力。
 *   - 戰鬥中不彈題。
 *
 * 用法 (scene 在「每次升級套用後」呼):
 *   const newly = detectWeaponEvolutions(mods, balance.weaponEvolution);
 *   for (const e of newly) this._showWeaponEvolutionFx(e); // 純 FX 提示
 *   // 之後每幀/每碰撞讀 mods 的進化旗標 (pierceCount/spreadBlastRadius/...) 施工。
 */

import type { RunModifiers } from './run-modifiers';
import type { WeaponEvolutionConfig, WeaponEvolutionDef } from '../data/balance-types';
import recipesJson from './weapon-evolution-recipes.json';

/** 進化觸發條件:同卡疊 N 次,或集滿兩卡組合 (各 ≥ N)。 */
export type EvolutionTrigger =
  | { kind: 'stack'; cardId: string; count: number }
  | { kind: 'combo'; cardIds: string[]; count: number };

/** 一條進化線的靜態配方 (給 UI / FX 顯示用,純資料,從 JSON 載入)。 */
export interface EvolutionRecipe {
  /** 進化線 id,對應 balance weaponEvolution.evolutions[].id。 */
  id: string;
  /** K-3 看得懂的進化名 (FX/標記用)。 */
  displayName: string;
  emoji: string;
  /** 一句因果說明 (給 toast/FX 副字)。 */
  blurb: string;
  /**
   * 進化演出用的 feedback preset 名。必須存在於
   * src/game/systems/feedback-presets.json (coverage 守門會抓)。
   */
  feedbackPreset: string;
  /** 觸發條件 (同卡疊 N / 兩卡組合)。 */
  trigger: EvolutionTrigger;
}

/** 從 JSON 載入的進化配方清單 (資料驅動,新增進化線 = 改 JSON,不改 TS)。 */
export const EVOLUTION_RECIPES: EvolutionRecipe[] = (
  recipesJson as { recipes: EvolutionRecipe[] }
).recipes;

/** 依 id 取進化配方 (FX 顯示用)。找不到回 undefined。 */
export function recipeById(id: string): EvolutionRecipe | undefined {
  return EVOLUTION_RECIPES.find((r) => r.id === id);
}

/**
 * 一條配方的觸發條件目前是否成立 (純讀 mods.pickCounts,不寫)。
 *   - stack:該卡被選次數 >= count。
 *   - combo:組合內「每一張」卡都被選 >= count 次。
 */
export function isRecipeSatisfied(recipe: EvolutionRecipe, m: RunModifiers): boolean {
  const t = recipe.trigger;
  const picked = (id: string): number => m.pickCounts[id] ?? 0;
  if (t.kind === 'stack') {
    return picked(t.cardId) >= t.count;
  }
  // combo:每張都要 ≥ count。
  return t.cardIds.every((id) => picked(id) >= t.count);
}

/**
 * 把某進化線的「進化後數值」(來自 balance config) 套進當局 RunModifiers。
 * idempotent 由呼叫端的 `weaponEvolutions` 去重保證 (同一條只套一次)。
 * 只寫進化旗標 / 累加當局數值,絕不碰 progress/修煉值。
 */
function applyEvolutionStats(m: RunModifiers, def: WeaponEvolutionDef): void {
  if (def.bulletDamageBonus > 0) m.evoBulletDamageBonus += def.bulletDamageBonus;
  if (def.pierceCount > 0) m.pierceCount += def.pierceCount;
  if (def.bounceBonus > 0) m.evoBounceBonus += def.bounceBonus;
  if (def.spreadBonus > 0) m.evoSpreadBonus += def.spreadBonus;
  // 散彈爆炸半徑:設「至少」(取 max),不累加 — 多條同設爆炸的進化不會無限放大。
  if (def.spreadBlastRadius > 0) {
    m.spreadBlastRadius = Math.max(m.spreadBlastRadius, def.spreadBlastRadius);
  }
}

/**
 * 偵測 + 套用所有「現在成立且尚未進化」的武器進化,回報「這次新進化」的清單。
 *
 * - 對每條「條件成立 && 尚未在 m.weaponEvolutions」的配方:從 balance config 取
 *   對應數值套進 mods (一次),並記進 m.weaponEvolutions (避免重套 / FX 重跳)。
 * - 缺 balance 對應數值的配方略過 (防呆,不進化)。
 *
 * 回傳:本次新進化的 EvolutionRecipe 陣列 (通常 0 或 1 條)。
 *
 * 鐵則:只讀/寫 RunModifiers + 讀 balance config。不碰 progress / 修煉值。
 */
export function detectWeaponEvolutions(
  m: RunModifiers,
  balance: WeaponEvolutionConfig,
): EvolutionRecipe[] {
  const newly: EvolutionRecipe[] = [];
  for (const recipe of EVOLUTION_RECIPES) {
    if (m.weaponEvolutions.includes(recipe.id)) continue; // 已進化,跳過。
    if (!isRecipeSatisfied(recipe, m)) continue;
    const def = balance.evolutions.find((e) => e.id === recipe.id);
    if (!def) continue; // balance 缺對應數值 → 不進化 (防呆)。
    applyEvolutionStats(m, def);
    m.weaponEvolutions.push(recipe.id);
    newly.push(recipe);
  }
  return newly;
}

/**
 * 升級選單「可進化!」標記:回傳此卡再選一次就會「觸發新進化」的配方 (純預覽,不寫)。
 *
 * 用法:upgrade-picker 渲染每張候選卡時呼,若回非 null → 卡上加「可進化!」徽章,
 * 讓 K-3 看到「再選這張就進化」的引導 (C2)。
 *
 * 判定:模擬「再選這張卡一次」後該配方是否「從未成立 → 成立」。只看「尚未進化」
 * 的配方 (已進化的不再標)。純讀,不改 mods。
 *
 * @returns 第一條「再選此卡即觸發」的配方;沒有則 null。
 */
export function evolutionPreviewForCard(
  cardId: string,
  m: RunModifiers,
): EvolutionRecipe | null {
  for (const recipe of EVOLUTION_RECIPES) {
    if (m.weaponEvolutions.includes(recipe.id)) continue; // 已進化,不再標。
    const t = recipe.trigger;
    const involvesCard =
      (t.kind === 'stack' && t.cardId === cardId) ||
      (t.kind === 'combo' && t.cardIds.includes(cardId));
    if (!involvesCard) continue;
    if (isRecipeSatisfied(recipe, m)) continue; // 已滿足 (但還沒套) — 不算「再選才觸發」。
    // 模擬 +1 此卡後是否滿足。
    const projected = simulatePick(m, cardId);
    if (isRecipeSatisfiedCounts(recipe, projected)) return recipe;
  }
  return null;
}

/** 複製 pickCounts 並對 cardId +1 (純函式,不改原 mods)。 */
function simulatePick(m: RunModifiers, cardId: string): Record<string, number> {
  const next: Record<string, number> = { ...m.pickCounts };
  next[cardId] = (next[cardId] ?? 0) + 1;
  return next;
}

/** 用一個 pickCounts 快照判定配方是否成立 (給 preview 模擬用)。 */
function isRecipeSatisfiedCounts(
  recipe: EvolutionRecipe,
  counts: Record<string, number>,
): boolean {
  const t = recipe.trigger;
  const picked = (id: string): number => counts[id] ?? 0;
  if (t.kind === 'stack') return picked(t.cardId) >= t.count;
  return t.cardIds.every((id) => picked(id) >= t.count);
}
